'use client';

import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Shadcn } from '@/components/ui/scope';
import { useAppSelector, useAppDispatch } from '@/store';
import {
  logout,
  hydrateFromStorage,
  setUser,
  setBuilder,
  setValidating,
  setSessionRestoring,
} from '@/store/slices/authSlice';
import { setCustomTheme, setMarketingTheme } from '@/store/slices/uiSlice';
import { useRouter, usePathname } from 'next/navigation';
import { API_BASE } from '@/lib/apiBase';

/**
 * The API client, fetched on demand.
 *
 * ── Why this is not a plain import ──────────────────────────────────────────
 * This component sits in the root layout, so it is in the initial bundle of
 * every page — including seventy marketing URLs whose whole job is to be fast
 * for a stranger on a phone. A static `import api from '@/utils/api'` put axios
 * (~24 KB gzipped) in the chunk shared by all of them, and measurement said
 * that was a quarter of what a text page downloaded.
 *
 * Nothing in that 24 KB is reachable without a session: every call below is
 * behind `token` or `sessionRestoring`, and `sessionRestoring` is only ever set
 * when there is a session hint cookie or a stored refresh token to restore
 * from. So a logged-out visitor downloaded an HTTP client, on every page, to
 * make no requests with it.
 *
 * Deferring it means axios arrives with the first authenticated call instead of
 * ahead of first paint. For a signed-in user that is one extra round trip on a
 * cold load, against a session that was about to fetch /auth/me anyway; for a
 * marketing visitor it never arrives at all.
 *
 * Resolved as the whole module so both the default export and the named helper
 * stay reachable.
 */
const apiModule = () => import('@/utils/api');

/** Refresh this far before expiry so a request never races the boundary. */
const REFRESH_LEAD_MS = 90_000;
/** On wake/refocus, anything expiring within this window is renewed at once. */
const WAKE_THRESHOLD_MS = 120_000;

// All marketing surfaces — anyone can read them without logging in.
const PUBLIC_EXACT = new Set<string>([
  '/',
  '/login',
  '/register',
  '/pricing',
  // Unlisted plans page — handed to a prospect directly, so it is absent from
  // the nav, the footer, sitemap.ts and robots.ts. It still has to render for a
  // logged-out visitor, or the link we send bounces them to /login.
  '/plans',
  '/security',
  '/contact',
  '/about',
  '/privacy',
  '/terms',
  '/cookie-policy',
  '/guides',
  // The tools hub. '/tools/' is in PUBLIC_PREFIXES for the calculators.
  '/tools',
  // Google Play requires a publicly reachable account-deletion URL — it must
  // render for a logged-out visitor (and for Play's own fetcher) or the
  // Data safety declaration is rejected.
  '/account-deletion',
  // The lifecycle walkthrough. Like /modules, an auth-gated marketing page
  // server-renders as a loading spinner with no crawlable content — so a
  // missing entry here is silent everywhere except in Google.
  '/how-it-works',
  '/vs-tally',
  '/vs-spreadsheet',
  // The module hub. Its CHILDREN are allowlisted by prefix below — that comment
  // used to say the hub "has no children any more", and when the fourteen
  // per-module landing pages came back the exact entry silently stopped
  // covering the branch. Every one of them server-rendered as a spinner.
  '/modules',
  // Long-form "every module, in full" overview — same public marketing surface
  // as the hub, and the page a salesperson sends a prospect before any signup.
  '/all-module',
  /**
   * The plan-expired explainer. It needs no session — it is where an expired
   * tenant lands, and the renewal email links straight to it — so bouncing a
   * logged-out visitor to /login showed them "Welcome back" and a password
   * field instead of telling them why they cannot get in. The page already
   * copes with an absent builder (it falls back to "Your plan has expired").
   */
  '/plan-expired',
  // The Tamil home page. Its children are covered by the '/ta/' prefix below.
  '/ta',
]);
// `/portal/…` and `/q/…` are public magic-link pages — the client / quote
// recipient has no account, so they must never be bounced to /login.
//
// `/modules/…` and `/construction-erp-software/…` are marketing branches: every
// page under them is a public landing page by construction, so they are matched
// by PREFIX rather than enumerated. An exact-match list is the wrong shape for a
// branch whose members come from a data file — adding a module or a city would
// otherwise require remembering this file, and forgetting is invisible in the
// browser (a logged-out human still sees the page once React hydrates) and fatal
// in Google (the crawler only ever sees the spinner). scripts/route-drift.mjs
// now fails the build if a sitemap URL is not public here.
const PUBLIC_PREFIXES = [
  '/status', '/set-password', '/reset-password', '/forgot-password', '/portal/', '/q/',
  '/modules/', '/construction-erp-software/',
  // The guides hub. '/guides' itself is in PUBLIC_EXACT; the trailing slash
  // here keeps this from matching anything else that starts with "guides".
  '/guides/',
  '/tools/',
  // The whole Tamil tree mirrors the English marketing paths under /ta, so one
  // prefix covers it — including pages added later. '/ta' itself is in
  // PUBLIC_EXACT; this prefix has the trailing slash so it cannot accidentally
  // match a future '/tasks' route.
  '/ta/',
];

const isPublic = (pathname: string) => {
  if (PUBLIC_EXACT.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
};

// Wrapped in <Shadcn> so its Tailwind utilities resolve — this loader renders
// app-wide (over still-MUI pages too), where global preflight is off. bg-background
// is theme-aware (the app mirrors dark mode onto `.dark`), matching the old
// light/dark backdrop.
function FullPageLoader() {
  return (
    <Shadcn className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/85 backdrop-blur-sm">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
    </Shadcn>
  );
}

export default function AuthChecker({ children }: { children?: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const { isHydrated, token, user, isValidating, tokenExpiresAt, sessionRestoring, impersonation } =
    useAppSelector((s) => s.auth);
  const router = useRouter();
  const pathname = usePathname();
  const bootstrapped = useRef(false);

  useEffect(() => {
    dispatch(hydrateFromStorage());
  }, [dispatch]);

  /**
   * Cold-start session restore.
   *
   * Access tokens only live 15 minutes, so a tab opened after any real gap
   * hydrates an expired one — or, on a brand-new tab, none at all. Whether a
   * restore is needed is decided in the hydrate reducer (so the route guard
   * never briefly sees "no token, not restoring"); this just performs it.
   */
  useEffect(() => {
    if (!isHydrated || !sessionRestoring || bootstrapped.current) return;
    bootstrapped.current = true;

    apiModule()
      .then(({ refreshAccessToken }) => refreshAccessToken())
      .catch(() => {
        // Cookie is gone or the session was revoked — fall through to the
        // route guard, which sends protected routes to /login. A failed chunk
        // load lands here too, and the same fallback is right for it.
        dispatch(logout());
      })
      .finally(() => dispatch(setSessionRestoring(false)));
  }, [isHydrated, sessionRestoring, dispatch]);

  /**
   * Proactive refresh. Renewing shortly before expiry means the user
   * essentially never hits a 401; the reactive path in api.ts stays as the
   * safety net for clock skew and suspended tabs.
   */
  useEffect(() => {
    // Never refresh mid-impersonation — that would swap the operator back into
    // their own identity while the UI still shows a tenant view.
    if (!token || !tokenExpiresAt || impersonation) return;

    const fire = () => {
      apiModule()
        .then(({ refreshAccessToken }) => refreshAccessToken())
        .catch(() => { /* 401 path handles it */ });
    };

    const delay = Math.max(5_000, tokenExpiresAt - Date.now() - REFRESH_LEAD_MS);
    const timer = window.setTimeout(fire, delay);

    // setTimeout doesn't fire while the machine is asleep, so re-check whenever
    // the tab becomes visible again or the network comes back.
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (tokenExpiresAt - Date.now() < WAKE_THRESHOLD_MS) fire();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [token, tokenExpiresAt, impersonation]);

  // Cross-tab session sync. The `storage` event fires in OTHER tabs when this
  // tab (or any tab) writes auth state. The logout reducer's comment assumed a
  // listener existed — it didn't, so logging out in one tab left the others
  // authed until their next 401. React to auth-key changes: pick up a cross-tab
  // login / token rotation via re-hydrate, or log out when the session is gone.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // e.key === null means storage.clear() — always re-evaluate.
      if (e.key !== null && e.key !== 'auth-storage') return;
      /**
       * A sign-out is decided by the EVENT, never by re-reading storage.
       *
       * `newValue === null` means another tab removed the session. Re-reading
       * localStorage to confirm is a race we lose intermittently: a response
       * still in flight in THIS tab (AuthChecker's own /auth/me) can call
       * persist() between the other tab's removeItem() and this handler
       * running. The re-read then finds a resurrected session and takes the
       * hydrate branch — the tab stays signed in against a revoked cookie.
       */
      if (e.key === 'auth-storage' && e.newValue === null) {
        dispatch(logout());
        return;
      }
      let hasSession = false;
      try {
        hasSession = !!localStorage.getItem('auth-storage');
      } catch { /* storage may be unavailable */ }
      if (hasSession) dispatch(hydrateFromStorage());
      else dispatch(logout());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  // Load the platform-wide MARKETING theme (super-admin marketing-theme page,
  // global Settings.theme) and apply it to the logged-out marketing/login
  // surfaces (`--mk-*`). Uses the PUBLIC endpoint so it works logged-out too.
  // NOTE: the per-tenant APP theme is separate — it arrives on `builder.theme`
  // from /auth/me below and drives `customTheme`.
  //
  // Plain `fetch`, not the axios client: for a logged-out visitor this is the
  // only request the page makes, it is a public GET, and it wants none of the
  // interceptor stack. Reaching for the client here is what put axios into
  // every marketing bundle. The root layout already resolves the same endpoint
  // server-side and publishes `--mk-*` from it, so this is the staleness
  // top-up rather than the primary read — failing quietly is correct.
  useEffect(() => {
    if (!isHydrated) return;
    const ac = new AbortController();
    fetch(`${API_BASE}/public/theme`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => dispatch(setMarketingTheme(body?.data?.theme || null)))
      .catch(() => { /* aborted, offline, or API down — committed default stands */ });
    return () => ac.abort();
  }, [isHydrated, dispatch]);

  // No session → drop any tenant app theme so a previous tenant's colours don't
  // linger on /login or other logged-out surfaces (marketing theme is separate).
  useEffect(() => {
    if (isHydrated && !token) dispatch(setCustomTheme(null));
  }, [isHydrated, token, dispatch]);

  useEffect(() => {
    if (!isHydrated) return;
    // A restore is in flight — the session may well be valid, so don't judge
    // it yet or we'd bounce a signed-in user to /login on every cold tab.
    if (sessionRestoring) return;

    // No token on a protected route → bounce to login
    if (!token && !isPublic(pathname)) {
      dispatch(logout());
      router.replace('/login');
      return;
    }

    // Have a token → validate it once on mount
    if (token) {
      let cancelled = false;
      /**
       * A token ROTATION re-runs this effect (see the dep array below), and a
       * rotation happens on a timer every ~13.5 minutes forever. That is a
       * background re-check of a session we already trust — not the cold
       * "we don't know who this is yet" case `isValidating` was built for.
       *
       * Raising the flag for it made the render gate at the bottom of this
       * component swap the entire app for a spinner every 13.5 minutes, which
       * unmounted every page: open drawers closed, filters reset, half-typed
       * forms cleared. Only the first check may block the UI.
       */
      const background = !!user;
      if (!background) dispatch(setValidating(true));
      apiModule()
        .then(({ default: api }) => api.get('/auth/me'))
        .then((res) => {
          if (cancelled) return;
          const data = res.data?.data;
          if (data?.user) dispatch(setUser(data.user));
          // Refresh the builder snapshot (modules may have changed) — but ONLY
          // when the response actually carries a `builder` field. A partial /
          // malformed response that omits it must not clobber a valid snapshot
          // (which would silently drop module gating + GST identity). A genuine
          // super-admin sends `builder: null`, so the key is present → honoured.
          if (data && 'builder' in data) {
            dispatch(setBuilder(data.builder ?? null));
            // The tenant's app theme rides on `builder.theme`. Apply it (null →
            // committed default preset). Super-admins have no builder → cleared.
            dispatch(setCustomTheme(data.builder?.theme ?? null));
          }
        })
        .catch(() => {
          // 401 is already handled by the axios interceptor
        })
        .finally(() => {
          if (!cancelled && !background) dispatch(setValidating(false));
        });
      return () => {
        cancelled = true;
      };
    }
    // Re-run when the token changes too (e.g. login on an already-mounted tree)
    // so user/builder are refreshed without a full remount. `pathname` is
    // intentionally excluded — the route guard below handles navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, token, sessionRestoring]);

  // Route guard: keep super-admins inside /admin, regular users out of it.
  // Exception: during impersonation, the swapped req.user is NOT super-admin,
  // so a super-admin who clicked "View as" lands in /home correctly.
  useEffect(() => {
    if (!isHydrated || !token || !user) return;
    const inAdmin = pathname.startsWith('/admin');
    if (user.superAdmin && !inAdmin && !isPublic(pathname)) {
      router.replace('/admin');
    } else if (!user.superAdmin && inAdmin) {
      router.replace('/home');
    }
  }, [isHydrated, token, user, pathname, router]);

  // Block rendering while we don't yet know if the session is valid.
  // - Before hydration: we can't even read the persisted token.
  // - While a cold tab trades its refresh cookie for a session — without this
  //   the guard above would flash the /login redirect before the restore lands.
  // - On a protected route, while validating a token against /auth/me.
  // Public routes never block (login/register/landing must render).
  const onPublic = isPublic(pathname);
  if (!onPublic) {
    if (!isHydrated) return <FullPageLoader />;
    if (sessionRestoring) return <FullPageLoader />;
    // `&& !user` is load-bearing, not defensive: with a user in the store the
    // session is known-good, so any validation in flight is a re-check and must
    // never take the screen away. Note this is a `return` — it REPLACES
    // `children`, so tripping it destroys all page state rather than flickering.
    if (token && isValidating && !user) return <FullPageLoader />;
  }

  return <>{children}</>;
}
